import { LoadingOutlined } from "@ant-design/icons"
import { Workflow } from "@finway-group/shared/lib/models"
import { Table } from "antd"
import { Key } from "antd/lib/table/interface"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"

import { ExpandableListItemIcon } from "Components/icons/ExpandableListItemIcon"
import DraggableTableWrapper from "Components/tables/draggableTableWrapper"
import { useIsPurchaseRequestEnabled, useIsTravelEnabled } from "Shared/hooks/featureFlags.hooks"
import { HandleDragEndParams, useDraggableTable, useTable } from "Shared/hooks/table.hooks"
import { NotificationService } from "Shared/services"
import { refetchTable, updateTableOnChange } from "Shared/store/actions/tables/tableActions"
import { fetchAllWorkflows, setWorkflow, updateWorkflow } from "Shared/store/actions/workflow/workflowActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"

import { PageBody } from "Components/page/pageBody"
import { DraggableRow } from "../../../components/tables/draggableRow"
import { getWorkflowColumns } from "./workflow.table.columns"
import { WorkflowDetails } from "./workflowDetails"

interface WorkflowTableInterface {
    setIsModalShowing: (arg: boolean) => void
}

const WorkflowTable = ({ setIsModalShowing }: WorkflowTableInterface) => {
    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const isPurchaseRequestEnabled = useIsPurchaseRequestEnabled()

    const [expandedRows, setExpandedRows] = useState<Array<Key>>([])

    useEffect(() => {
        if (id) {
            setExpandedRows([id])
        }
    }, [id])

    const workflowsTable = useTable<Workflow>(TablesEnum.WORKFLOWS)

    const isTravelEnabled = useIsTravelEnabled()

    const onEditWorkflow = async (id: string) => {
        const workflow = workflowsTable.data.docs.find((workflow) => workflow._id === id)
        await setWorkflow(workflow)(dispatch)
        setIsModalShowing(true)
    }

    const columns = getWorkflowColumns({
        t,
        onEditWorkflow,
        isTravelEnabled,
        isPurchaseRequestEnabled,
        dispatch,
    })

    const handleDragEnd = ({ activeIndex, overIndex }: HandleDragEndParams<Workflow>) => {
        const draggedWorkflow = workflowsTable.data.docs[activeIndex]
        const targetWorkflow = workflowsTable.data.docs[overIndex]

        const updatedWorkflow = { ...draggedWorkflow, order: targetWorkflow?.order }

        // Start performing updates to server
        const performUpdate = async () => {
            await updateWorkflow(updatedWorkflow?._id, updatedWorkflow)(dispatch)
            await fetchAllWorkflows()(dispatch)
            dispatch(refetchTable(TablesEnum.WORKFLOWS))
        }

        return performUpdate().catch((e) => NotificationService.showErrorNotificationBasedOnResponseError(e, t("error:update.title"), t("error:update.message")))
    }

    const { onDragEnd, dataSource, isLoading, onDragStart, activeId } = useDraggableTable({ dataSource: workflowsTable.data.docs, handleDragEnd })

    const tableLoading = {
        spinning: isLoading,
        indicator: <LoadingOutlined style={{ fontSize: 30, color: "#757575" }} spin />,
    }

    return (
        <DraggableTableWrapper dataSource={dataSource} onDragEnd={onDragEnd} columns={columns} onDragStart={onDragStart} activeId={activeId}>
            <PageBody>
                <Table
                    data-testid="workflowTable"
                    rowKey={(workflow: Workflow) => workflow._id}
                    showHeader={false}
                    columns={columns}
                    dataSource={dataSource}
                    components={{
                        body: {
                            row: DraggableRow,
                        },
                    }}
                    pagination={{
                        position: ["bottomRight"],
                        showSizeChanger: true,
                        current: workflowsTable.data.page,
                        pageSize: workflowsTable.data.limit,
                        hideOnSinglePage: false,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                    onChange={updateTableOnChange(TablesEnum.WORKFLOWS, workflowsTable.data, false)}
                    expandable={{
                        expandRowByClick: true,
                        expandIconColumnIndex: 1,
                        expandedRowRender: (workflow: Workflow) => <WorkflowDetails workflow={workflow} />,
                        expandedRowKeys: expandedRows,
                        defaultExpandedRowKeys: expandedRows,
                        rowExpandable: () => true,
                        expandIcon: ({ expanded, onExpand, record }) => (
                            <button className="outline-none" onClick={(e) => onExpand(record, e)}>
                                <ExpandableListItemIcon expanded={expanded} />
                            </button>
                        ),
                        onExpandedRowsChange: (expandedRows: Array<Key>) => setExpandedRows(expandedRows),
                    }}
                    loading={tableLoading}
                />
            </PageBody>
        </DraggableTableWrapper>
    )
}

export default WorkflowTable
